/* eslint-disable camelcase */
import axios from 'axios';
import { useFormik } from 'formik';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { ButtonGroup, Card, Col, Form, Image, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SlidingPane from "react-sliding-pane";
import * as Yup from 'yup';
import formatDate from '../../../../../frontend/components/components/utils/formatDate';
import Button from '../../../../shared/components/Button';
import Dropzone, { DROPZONE_STATE } from '../../../../shared/components/Dropzone/Dropzone';
import FormControl from '../../../../shared/components/Form/FormControl';
import ResponsiveHeader from '../../../../shared/components/ResponsiveHeader';
import { ACTIONS } from '../../../../utils/constants';

/**
 *
 * @param {{
 * open: boolean
 * post: any
 * onRequestClose: func
 * onAfterClose: onAfterClose
 * }} params
 * @returns
 */
const OtherDeliverablesSidePane = ({
  open,
  post,
  onRequestClose,
  onAfterClose,
  status,
}) => {
  const { t } = useTranslation();
  const offer = useSelector(store => get(store, 'offer.offer'));

  const validationSchema = Yup.lazy((values) => Yup.object({
    mediaUrl: values.mediaRequired
      ? Yup.mixed().required(t('influencers.common.forms.is_required'))
      : Yup.string().trim().nullable(),
    caption: values.captionRequired
      ? Yup.string().trim().required(t('influencers.common.forms.is_required'))
      : Yup.string().trim().nullable(),
    url: values.urlRequired
      ? Yup.string().trim().required(t('influencers.common.forms.is_required'))
      : Yup.string().trim().nullable(),
    captionRequired: false,
    mediaRequired: false,
    urlRequired: false,
  }));

  const [state, setState] = useState({
    loading: false,
    success: undefined,
    editing: !!post,
    showDeletePrompt: false,
    action: null
  });

  const formik = useFormik({
    initialValues: {
      caption: get(post, 'caption', '') || '',
      url: get(post, 'url', '') || '',
      mediaUrl: get(post, 'media_url', '') || '',

      mediaRequired: offer.other_deliverable_media_required,
      urlRequired: offer.other_deliverable_url_required,
      captionRequired: offer.other_deliverable_caption_required
    },
    validationSchema,
    onSubmit: values => {
      setState({ ...state, loading: true });

      const user_post = {};
      if (offer.other_deliverable_media_required) user_post.media_url = values.mediaUrl;
      if (offer.other_deliverable_caption_required) user_post.caption = values.caption;
      if (offer.other_deliverable_url_required) user_post.url = values.url;

      const data = {
        user_post
      };
      let promise = state.editing
        ? axios.put(`/api/v1/influencers/offers/${offer.id}/user_posts/${post.id}`, data)
        : axios.post(`/api/v1/influencers/user_offers/${offer.user_offer.id}/user_posts`, data);

      promise.then(({ data }) => {
        console.debug(data);
        if (data.success) {
          setState({
            ...state,
            success: true,
            action: state.editing ? ACTIONS.UPDATE : ACTIONS.CREATE
          });
        } else {
          throw 'Could not upload';
        }
        onRequestClose();
      }).catch((err) => {
        console.error(err);
        if (state.editing) {
          setState({
            ...state,
            loading: false
          });
        } else {
          setState({
            ...state,
            success: false,
            loading: false
          });
        }
        formik.resetForm();
      });
    },
  });

  const renderForm = useCallback(() => {
    let showMedia = offer.other_deliverable_media_required;
    let showCaption = offer.other_deliverable_caption_required;
    let showLink = offer.other_deliverable_url_required;

    // if (OFFER_STAGE.POST_CONTENT.includes(get(offer, 'user_offer.status'))) {
    //   showMedia = true;
    //   showCaption = true;
    //   showLink = true;
    // }

    return <React.Fragment>
      <div className="mt-3"></div>
      {
        showMedia
        && <Form.Group>
          <Form.Label>
            {t('influencers.pages.campaign.drafts.media')}
          </Form.Label>
          <div>
            {get(formik.values, 'mediaUrl', '') && <Image fluid src={formik.values.mediaUrl} />}
            {!['view_draft', 'view_feedback'].includes(status) &&
              <Dropzone
                className="mb-3 mt-2"
                name='mediaUrl'
                selectFiles
                onChange={(event, fileState) => {
                  if (event === DROPZONE_STATE.SUCCESS && fileState.success) {
                    formik.setFieldValue('mediaUrl', fileState.fileUrl);
                  }
                }}
              />
            }
          </div>
        </Form.Group>
      }

      {
        showCaption && <Form.Group>
          <Form.Label>
            {t('influencers.common.caption')}
          </Form.Label>
          {['view_draft', 'view_feedback'].includes(status) ?
            <p>{get(formik.values, 'caption', '')}</p> :
            <FormControl
              placeholder={t('influencers.common.text')}
              textarea
              name='caption'
              defaultValue={formik.values.caption}
              rows={5}
              onChange={formik.handleChange}
            />
          }
        </Form.Group>
      }

      {
        showLink && <Form.Group>
          <Form.Label>
            {t('influencers.common.link')}
          </Form.Label>
          {['view_draft', 'view_feedback'].includes(status) ?
            <a href={get(formik.values, 'url', '')} target="_blank" rel="noreferrer">{get(formik.values, 'url', '')}</a> :
            <FormControl
              placeholder={t('influencers.common.text')}
              name='url'
              defaultValue={formik.values.url}
              onChange={formik.handleChange}
            />
          }
        </Form.Group>
      }

    </React.Fragment>;
  }, [formik, state, onRequestClose]);

  const renderFeedback = useCallback(() => {
    if (get(post, 'versions.length', false)) {
      return post.versions.map(version => (
        <div key={version.id}>
          <div className="body-regular-3 mb-3">
            {formatDate(version.created_at)}
          </div>
          <div className="body-regular-1">
            {version.am_current_status_note}
          </div>
        </div>
      ));
    } else if (get(post, 'am_current_status_note', false)) {
      return (
        <div>
          <div className="body-regular-1">
            {post.am_current_status_note}
          </div>
        </div>
      );
    }

    return <Card className="d-flex align-items-center justify-content-center empty px-4">
      {t('influencers.pages.campaign.drafts.no_feedback')}
    </Card>;
  }, [post]);

  const onDeleteDraft = useCallback(() => {
    axios.delete(`/api/v1/influencers/offers/${offer.id}/user_posts/${post.id}`).then(() => {
      setState({ ...state, success: true, action: ACTIONS.DELETE });
      onRequestClose();
    });
  }, []);

  const renderHeader = useCallback(() => state.editing
    ? <ResponsiveHeader xs='h4' sm='h3'
      text={`${t('influencers.pages.campaign.other_deliverables.edit_other_deliverables')}
      ${offer.other_deliverable_label}`} />
    : <ResponsiveHeader xs='h4' sm='h3'
      text={`${t('influencers.pages.campaign.other_deliverables.submit_other_deliverables')}
      ${offer.other_deliverable_label}`} />
    , [state.editing]);

  const renderDeletePrompt = useCallback(() => <div>
    <ResponsiveHeader xs='h4' lg='h3' text={t('influencers.common.delete')} />
    <div className="body-regular-1 mt-4 mb-6">
      {t('influencers.pages.campaign.drafts.delete_prompt_message')}
    </div>
    <Row className='justify-content-end'>
      <Col className="col-auto">
        <ButtonGroup className="non-adaptable">
          <Button
            variant="secondary"
            type="button"
            onClick={() => setState({
              ...state,
              showDeletePrompt: false
            })}>
            {t('influencers.common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={onDeleteDraft}
            disabled={state.loading}>
            {state.loading
              ? <Spinner animation="border" size="sm" />
              : t('influencers.common.delete')
            }
          </Button>
        </ButtonGroup>
      </Col>
    </Row>
  </div>, [post]);

  return (
    <SlidingPane
      overlayClassName="slide-over"
      isOpen={open}
      closeIcon={null}
      width="375"
      shouldCloseOnEsc
      onRequestClose={onRequestClose}
      onAfterClose={() => onAfterClose(state.success, state.action)}
      hideHeader
      from={document.body.dir === 'ltr' ? 'right' : 'left'}
    >
      {state.showDeletePrompt ?
        renderDeletePrompt()
        : <div>
          {renderHeader()}

          <Form onSubmit={formik.handleSubmit}>
            {state.editing ?
              <Tabs
                className="not-link my-5"
                bg="light"
                expand="xs"
                defaultActiveKey={status == 'view_feedback' ? 'feedback' : 'content'}
              >
                <Tab eventKey="content" title={t('influencers.pages.campaign.drafts.content')}>
                  {renderForm()}
                </Tab>
                <Tab eventKey="feedback" title={t('influencers.pages.campaign.drafts.feedback')}>
                  <React.Fragment>
                    {renderFeedback()}
                  </React.Fragment>
                </Tab>
              </Tabs>
              : renderForm()
            }

            <div className="my-6">
              <hr />
            </div>
            <Row className='justify-content-end'>
              <Col className="col-auto">
                <ButtonGroup className="non-adaptable">
                  {
                    !['view_draft', 'view_feedback'].includes(status) && state.editing &&
                    <Button
                      variant="tertiary"
                      type="button"
                      onClick={() => setState({ ...state, showDeletePrompt: true })}
                      disabled={state.loading}>
                      {t('influencers.common.delete')}
                    </Button>
                  }
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => onRequestClose()}
                    disabled={state.loading}>
                    {t('influencers.common.cancel')}
                  </Button>
                  {!['view_draft', 'view_feedback'].includes(status) &&
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={
                        (state.loading || !formik.isValid || !formik.dirty)
                      }>
                      {state.loading
                        ? <Spinner animation="border" size="sm" />
                        : t('influencers.common.save')
                      }
                    </Button>
                  }
                </ButtonGroup>
              </Col>
            </Row>
          </Form>
        </div>
      }
    </SlidingPane>
  );
};

OtherDeliverablesSidePane.propTypes = {
  open: PropTypes.bool,
  post: PropTypes.any,
  onRequestClose: PropTypes.func.isRequired,
  onAfterClose: PropTypes.func,
};

OtherDeliverablesSidePane.defaultProps = {
  open: false,
  post: null,
  onAfterClose: () => { },
};

export default OtherDeliverablesSidePane;
